import PropTypes from 'prop-types';
import { useMemo, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import {
	useDeleteOrderPerformanceRuleMutation,
	useReMatchOrderPerformanceRulesMutation,
	useUpdateOrderPerformanceRuleMutation,
} from '../../../../../slices/ordersApiSlice';
import EventModalMediaContent from '../../../../../components/EventModalMediaContent';
import AppScrollArea from '../../../../../components/common/AppScrollArea';
import { useOrderContext } from '../../../../../providers/OrderProvider';
import { getOrderPerformanceBasisView } from '../../../../../utils/order/orderPerformanceBasisView';
import { useOrderPerformanceFeatures } from '../../../../../providers/OrderPerformanceFeaturesProvider';
import OrderPerformanceRuleDetailSection from './OrderPerformanceRuleDetailSection';
import PerformanceRulesRematchOverlay, {
	getPerformanceRulesBusyLabel,
	isPerformanceRulesBusy,
} from './PerformanceRulesRematchOverlay';
import OrderPerformanceRulesRematchHoldProvider, {
	useOrderPerformanceRulesRematchHold,
} from '../../../../../providers/OrderPerformanceRulesRematchHoldProvider';

const ROLE_LABELS = {
	assistant: '销售助理',
	sale: '销售员',
	salesSupervisor: '销售主管',
};

const RULE_TYPE_LABELS = {
	fixed: '固定金额',
	percentage: '百分比',
	profitMarginRanges: '利润率分段',
};

const formatRuleScopeLabel = (rule, roleKey) => {
	if (roleKey === 'assistant' && (rule?.matchReason === 'order_level' || ((rule?.brand || '全部') === '全部' && (rule?.category || '全部') === '全部'))) {
		return '整单';
	}
	if (roleKey === 'salesSupervisor' && rule?.matchReason === 'no_supervisor') {
		return '无销售主管';
	}
	return `${rule?.brand || '全部'} / ${rule?.category || '全部'}`;
};

const getDivergedRuleDetails = (order) => {
	const snapshot = order?.performanceRulesSnapshot ?? {};
	const roleRules = [
		...(snapshot.assistantRules ?? []).map((r) => ({ ...r, role: 'assistant' })),
		...(snapshot.saleRules ?? []).map((r) => ({ ...r, role: 'sale' })),
		...(snapshot.salesSupervisorRules ?? []).map((r) => ({ ...r, role: 'salesSupervisor' })),
	];

	const rows = roleRules.filter((rule) => rule?.divergedFromGlobalRule);
	const labels = rows.map((rule) => {
		const roleLabel = ROLE_LABELS[rule.role] ?? rule.role ?? '未知角色';
		const ruleTypeLabel = RULE_TYPE_LABELS[rule.ruleType] ?? rule.ruleType ?? '规则';
		const scopeLabel = formatRuleScopeLabel(rule, rule.role);
		return `${roleLabel} · ${ruleTypeLabel} · ${scopeLabel}`;
	});

	return Array.from(new Set(labels));
};

const formatBasisIssueDetail = (issue) => {
	if (issue.problem === 'missing') return '缺少绩效规则';
	if (issue.scopes.length === 0) return '存在未匹配项';
	if (issue.scopes.length <= 3) return `未匹配：${issue.scopes.join('、')}`;
	return `未匹配：${issue.scopes.slice(0, 3).join('、')} 等 ${issue.scopes.length} 条`;
};

const StatusDot = ({ variant = 'warning' }) => (
	<span className={`d-inline-block rounded-circle me-2 ${variant === 'danger' ? 'bg-danger' : 'bg-warning'}`} style={{ width: 6, height: 6, verticalAlign: 'middle' }} />
);

const OrderPerformanceRulesSectionBody = ({ embedded = false, forceVisible = false }) => {
	const { orderDetailUi } = useOrderPerformanceFeatures();
	const { order, isOrderReady } = useOrderContext();
	const { rematchRenderHold } = useOrderPerformanceRulesRematchHold();
	const canViewSettlement = Boolean(order?.viewerCapabilities?.canViewPerformanceSettlement);
	const [showRulesModal, setShowRulesModal] = useState(false);
	const [, { isLoading: isRematching }] = useReMatchOrderPerformanceRulesMutation();
	const [, { isLoading: isUpdating }] = useUpdateOrderPerformanceRuleMutation();
	const [, { isLoading: isDeleting }] = useDeleteOrderPerformanceRuleMutation();
	const isRematchingUi = isRematching || rematchRenderHold;
	const performanceRulesBusy = isPerformanceRulesBusy({ isRematching: isRematchingUi, isUpdating, isDeleting });
	const performanceRulesBusyLabel = getPerformanceRulesBusyLabel({ isRematching: isRematchingUi, isUpdating, isDeleting });

	const orderOId = order?._id;
	const { hasBasis, hasUnmatched, unmatchedIssues: unmatchedBasisIssues } = getOrderPerformanceBasisView(order);
	const performanceRulesDiverged = Boolean(order?.performanceRulesDivergedFromGlobal);
	const isFinalCostConfirmed = Boolean(order?.isFinalCostConfirmed);
	const divergedRuleDetails = useMemo(() => getDivergedRuleDetails(order), [order]);
	const performanceRulesDivergedHint = isFinalCostConfirmed ? '当前某规则仅对本单生效' : '绩效规则已变化';
	const hasStaleDefaultMatch = useMemo(() => {
		const snapshot = order?.performanceRulesSnapshot ?? {};
		const roleRules = [...(snapshot.saleRules ?? []), ...(snapshot.salesSupervisorRules ?? [])];
		return roleRules.some((rule) => rule?.divergedFromGlobalRule && rule?.usedDefaultRule);
	}, [order]);

	const performanceMatchContent = useMemo(() => {
		const blocks = [];

		if (!hasBasis) {
			blocks.push(
				<div key='no-basis' className='text-danger-emphasis fw-semibold'>
					<StatusDot variant='danger' />
					尚未配置绩效规则，请先添加或重匹配
				</div>,
			);
		} else if (hasUnmatched && unmatchedBasisIssues.length > 0) {
			blocks.push(
				<div key='unmatched'>
					<div className='text-danger-emphasis fw-semibold'>
						<StatusDot variant='danger' />
						以下绩效规则未匹配齐全，请先完成匹配后再确认最终成本
					</div>
					<ul className='list-unstyled mb-0 mt-1 ps-3 text-muted'>
						{unmatchedBasisIssues.map((issue) => (
							<li key={issue.role} className='py-0'>
								<span className='text-danger fw-semibold'>{issue.roleLabel}</span>：{formatBasisIssueDetail(issue)}
							</li>
						))}
					</ul>
				</div>,
			);
		}

		if (performanceRulesDiverged) {
			blocks.push(
				<div key='diverged'>
					<div className='text-warning-emphasis fw-semibold'>
						<StatusDot variant='warning' />
						{performanceRulesDivergedHint}
					</div>
					{hasStaleDefaultMatch ? <p className='fs-10 text-muted mb-0 mt-1'>部分品牌曾使用默认规则，全局已可匹配专属规则，请重匹配。</p> : null}
					{divergedRuleDetails.length > 0 ? (
						<ul className='list-unstyled mb-0 mt-1 ps-3 text-muted'>
							{divergedRuleDetails.slice(0, 3).map((detail) => (
								<li key={detail} className='py-0'>
									- {detail}
								</li>
							))}
							{divergedRuleDetails.length > 3 ? <li>- 等 {divergedRuleDetails.length} 条</li> : null}
						</ul>
					) : null}
				</div>,
			);
		}

		if (blocks.length === 0) return null;
		return <div className='d-flex flex-column gap-2 fs-10 text-600'>{blocks}</div>;
	}, [divergedRuleDetails, hasBasis, hasStaleDefaultMatch, hasUnmatched, performanceRulesDiverged, performanceRulesDivergedHint, unmatchedBasisIssues]);

	if (!canViewSettlement || (!orderDetailUi && !forceVisible) || !orderOId || order?.uiHints?.isLegacyCompletedBeforeFinalCostCutoff) return null;

	const performanceMatchAction = (
		<Button
			size='sm'
			className='fs-11'
			variant='outline-warning'
			disabled={!isOrderReady || performanceRulesBusy}
			onClick={() => setShowRulesModal(true)}
		>
			查看
		</Button>
	);

	return (
		<>
			<EventModalMediaContent icon='hand-holding-dollar' heading='绩效规则' headingAction={performanceMatchAction}>
				{performanceMatchContent ?? <div className='fs-10 text-muted'>当前绩效规则已匹配</div>}
			</EventModalMediaContent>

			<Modal
				show={showRulesModal}
				onHide={() => !performanceRulesBusy && setShowRulesModal(false)}
				centered
				size='lg'
				className='modal-nested-stack'
				backdropClassName='modal-backdrop-nested'
			>
				<Modal.Header closeButton={!performanceRulesBusy} className='border-bottom'>
					<Modal.Title className='fs-6'>绩效规则</Modal.Title>
				</Modal.Header>
				<Modal.Body className='fs-10 position-relative' style={{ minHeight: performanceRulesBusy ? '12rem' : undefined }}>
					<PerformanceRulesRematchOverlay show={performanceRulesBusy} label={performanceRulesBusyLabel} />
					<AppScrollArea maxHeight='70vh'>
						<OrderPerformanceRuleDetailSection embedded={embedded} />
					</AppScrollArea>
				</Modal.Body>
			</Modal>
		</>
	);
};

OrderPerformanceRulesSectionBody.propTypes = {
	embedded: PropTypes.bool,
	forceVisible: PropTypes.bool,
};

const OrderPerformanceRulesSection = (props) => (
	<OrderPerformanceRulesRematchHoldProvider>
		<OrderPerformanceRulesSectionBody {...props} />
	</OrderPerformanceRulesRematchHoldProvider>
);

OrderPerformanceRulesSection.propTypes = {
	embedded: PropTypes.bool,
	forceVisible: PropTypes.bool,
};

export default OrderPerformanceRulesSection;
